import type { Damage, UpdateDamageLineBody } from '@/types';
import type { CarDamageAreaCode } from '@/utils/constants';

export type StagedDamageEdit = {
  body: UpdateDamageLineBody;
  display: Partial<Damage>;
  salesGradeValue?: number;
  valueForGrading?: number | null;
};

export const optionLabel = (
  options: Array<{ label?: unknown; value?: unknown }> | undefined,
  value: unknown,
): string | undefined => {
  if (value == null || value === '') return undefined;
  const match = options?.find(
    (item) => item.value === value || String(item.value) === String(value),
  );
  if (match?.label == null || match.label === '') return undefined;
  return String(match.label);
};

/** Sector indices are 1-based. The grid stores a 9-character 0/1 mask. */
export const sectorMaskToIndices = (sector?: string | null): number[] => {
  if (!sector) return [];
  const indices: number[] = [];
  for (let index = 0; index < sector.length && index < 9; index += 1) {
    if (sector[index] === '1') indices.push(index + 1);
  }
  return indices;
};

export const sectorIndicesToMask = (indices: number[]): string => {
  const chars = Array.from({ length: 9 }, () => '0');
  indices.forEach((index) => {
    if (index >= 1 && index <= chars.length) chars[index - 1] = '1';
  });
  return chars.join('');
};

export const inlineDamageToUpdateBody = (
  damage: Damage,
  inspectionId: number,
  siteId: number,
  changes?: Partial<Damage>,
): UpdateDamageLineBody => {
  const merged = { ...damage, ...changes };
  const repairMethodCode =
    changes?.repairActionCode !== undefined
      ? String(changes.repairActionCode ?? '')
      : String(merged.repairActionCode ?? '');

  return {
    damageLineId: damage.id,
    inspectionId,
    siteId,
    areaCode: String(merged.areaCode ?? '') as CarDamageAreaCode,
    partCode: String(merged.partCode ?? ''),
    damageCauseCode: String(merged.damageCauseCode ?? ''),
    sectorIndices: merged.sectorIndices?.length
      ? merged.sectorIndices
      : sectorMaskToIndices(merged.sector),
    damageConditionCode: String(merged.damageConditionCode ?? ''),
    severityId:
      merged.severityId == null || merged.severityId === ('' as unknown)
        ? undefined
        : Number(merged.severityId),
    repairMethodCode,
    internalGradingId: Number(merged.internalGradingId ?? 0),
    workCenterCode: String(merged.workCenterCode ?? ''),
    responsibilityCode: String(merged.responsibilityCode ?? ''),
    responsibilityShortcutCode: merged.responsibilityShortcutCode,
    dealerCode: merged.dealerCode,
    note: String(merged.note ?? ''),
    mechanicalFailure: String(merged.mechanicalFailure ?? ''),
  };
};

/** Part or damage type differs from the saved line. That unlocks severity and repair. */
export const hasClassificationChange = (
  saved: {
    partCode?: string | null;
    damageConditionCode?: string | null;
  },
  current: {
    partCode?: string | null;
    damageConditionCode?: string | null;
  },
) =>
  String(current.partCode ?? '') !== String(saved.partCode ?? '') ||
  String(current.damageConditionCode ?? '') !==
    String(saved.damageConditionCode ?? '');

export const mergeInlineIntoDamageBody = (
  body: UpdateDamageLineBody,
  changes?: Partial<Damage>,
): UpdateDamageLineBody => {
  if (!changes) return body;
  return {
    ...body,
    ...(changes.partCode !== undefined
      ? { partCode: String(changes.partCode ?? '') }
      : {}),
    ...(changes.damageConditionCode !== undefined
      ? { damageConditionCode: String(changes.damageConditionCode ?? '') }
      : {}),
    ...(changes.severityId !== undefined
      ? {
          severityId:
            changes.severityId == null ? undefined : Number(changes.severityId),
        }
      : {}),
    ...(changes.repairActionCode !== undefined
      ? { repairMethodCode: String(changes.repairActionCode ?? '') }
      : {}),
    ...(changes.workCenterCode !== undefined
      ? { workCenterCode: String(changes.workCenterCode ?? '') }
      : {}),
  };
};
